import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  BreadcrumbHeading,
  EllipsisMenu,
  Plus,
  PrimaryButton,
  Spinner,
  Table,
  useNotifications,
  ViewIcon,
} from '@weareopenr/component-library';
import { getBrands } from '../../api/getBrands/getBrands';
import { useQuery } from '@tanstack/react-query';
import styles from './Dashboard.module.scss';
import BrandModal from 'src/components/BrandModal';
import { ErrorMessages } from '../../api/constants';
import { usePermissions, useUserTracking } from 'src/hooks';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { IBrandResponse } from 'src/api/getBrands/types';
import { getLogo } from 'src/utils/getLogo';
import { Logo } from 'src/components/Logo/Logo';

const renderLogo = (info: CellContext<IBrandResponse, unknown>) => {
  return <Logo src={getLogo(info.row.original.logo_url, '')} name={info.row.original.name} />;
};

const renderChannels = (info: CellContext<IBrandResponse, unknown>) =>
  info.row.original.configured_addons.length;

export const Dashboard = () => {
  const setMessage = useNotifications();
  const history = useHistory();
  const [openBrandModal, setOpenBrandModal] = useState<boolean>(false);
  const { canCreateBrands } = usePermissions();
  const { trackEvent } = useUserTracking();

  const handleModalToggle = (): void => {
    setOpenBrandModal((prev) => !prev);
    if (!openBrandModal) {
      trackEvent('brand_create_modal_opened');
    }
  };

  const onView = (brandId: string): void => {
    history.push(`/brands/${brandId}`);
  };

  const renderActions = (info: CellContext<IBrandResponse, unknown>) => (
    <EllipsisMenu
      variant="ghost"
      hoverStyle="high-contrast"
      options={[
        {
          id: 1,
          name: 'View',
          icon: ViewIcon,
          onOptionClick: () => onView(info.row.original.brand_id),
        },
      ]}
    />
  );
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['brands'],
    queryFn: getBrands,
    onError: (e: Error) => {
      setMessage({
        message: e.message,
        severity: 'error',
        description: ErrorMessages.CONTACT_ADMIN,
      });
    },
  });

  const columnHelper = createColumnHelper<IBrandResponse>();

  const columns = [
    columnHelper.display({ id: 'logo', size: 80, cell: renderLogo }),
    columnHelper.accessor('name', {
      header: 'Brand',
    }),
    columnHelper.display({
      id: 'extensions',
      header: 'Extensions',
      size: 100,
      cell: renderChannels,
    }),
    columnHelper.accessor('venues', { header: 'Venues', size: 80 }),
    columnHelper.display({ id: 'actions', size: 80, cell: renderActions }),
  ];

  return (
    <>
      {isLoading ? (
        <div className={styles.spinner}>
          <Spinner size="xlarge" colour="rainbow" />
        </div>
      ) : (
        <>
          <div className={styles.header}>
            <BreadcrumbHeading
              breadcrumbs={[{ label: 'Brands', path: '/brands' }]}
              primaryButton={
                canCreateBrands ? (
                  <PrimaryButton icon={Plus} onClick={handleModalToggle} aria-label="Create Brand">
                    Create Brand
                  </PrimaryButton>
                ) : undefined
              }
            />
          </div>
          <BrandModal
            open={openBrandModal}
            closeModal={handleModalToggle}
            refetch={refetch}
            brands={data}
            variant="create"
          />
          {data && <Table columns={columns} data={data} />}
        </>
      )}
    </>
  );
};
